import React, { useState, useMemo } from 'react';
import PromotionDetailPresenter from './PromotionDetailPresenter';
import { useCreateCart, useUpdateCart } from 'hooks/CartHooks';
import { useCreateWishlist, useDeleteWishlist } from 'hooks/WishlistHooks';
import {
  useDefaultShopDataStack,
  useUserDataAction,
  useUserDataStack,
  useUserStack,
} from 'store';
import MessageAlert, { calculateDiscount, STORE_URL } from 'utils';
import lodash from 'utils/Lodash';
import { useGetPromotion } from 'hooks/PromotionHooks';
import { useParams } from 'react-router-dom';
import { decrypt } from 'utils/Crypto';
import { useGetGroupProduct } from 'hooks/GroupProductHooks';

const TAP = [
  { key: 'DESC-comment', label: '판매인기순' },
  { key: 'ASC-finalPrice', label: '낮은가격순' },
  { key: 'DESC-finalPrice', label: '높은가격순' },
  { key: 'DESC-score', label: '상품평순' },
  { key: 'DESC-create', label: '등록일순' },
];

const PromotionDetailContainer = (props) => {
  const { navigate, location, screenSize, type = 'promotion' } = props;

  /* ===== Route ===== */
  const { promotionId } = useParams();

  /* ===== Mutate ===== */
  const { createCart } = useCreateCart();
  const { updateCart } = useUpdateCart();
  const { createWishlist } = useCreateWishlist();
  const { deleteWishlist } = useDeleteWishlist();

  /* ===== Data ===== */
  const IDX = React.useMemo(() => {
    const decodeIdx = decrypt(promotionId);
    return Number(decodeIdx);
  }, [promotionId]);

  const options = {
    initialData: null,
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return null;

      return data;
    },
  };

  // NOTE: 프로모션
  const { data: Promotion } = useGetPromotion(IDX, null, {
    enabled: IDX && type === 'promotion',
    ...options,
  });
  // NOTE: 그룹상품
  const { data: Group } = useGetGroupProduct(IDX, null, {
    enabled: IDX && type === 'recipe',
    ...options,
  });

  const userInfo = useUserStack();
  const { cartList, wishlist } = useUserDataStack();
  const userDataAction = useUserDataAction();
  const { products, policies, promotion, settings, shops } =
    useDefaultShopDataStack();

  const [modalData, setModalData] = useState({ type: '', data: null });

  const handleSubmit = async (t = 'cart', d) => {
    let mutateData = {};
    switch (t) {
      case 'cart': {
        mutateData = {
          user_id: userInfo.user_id,
          product_id: d.key,
          cart_num: d.cartNum,
          shop_information_id: d.shopKey,
          product_option: d.product_option ?? null,
          cart_type: d.type,
          regular_cycle: d.regular_cycle,
        };

        const existing = cartList.filter((cart) => cart.product_id === d.key);
        if (!existing.length) {
          await handleMutate(createCart, mutateData).then((res) => {
            if (res.status !== 200)
              return MessageAlert.warning('장바구니 추가에 실패하였습니다.');
            MessageAlert.success('장바구니에 추가되었습니다.');
            userDataAction.addData('cartList', res.data);
          });
        } else {
          const isSame = existing.find((cart) => {
            const {
              cart_num,
              is_fixed,
              created_at,
              updated_at,
              cart_id,
              ...rest
            } = cart;
            const { cart_num: newCartNum, ...newRest } = mutateData;
            return lodash.isObjEqual(rest, newRest);
          });

          if (isSame) {
            mutateData = { ...isSame, cart_num: isSame.cart_num + d.cartNum };
            await handleMutate(updateCart, mutateData).then((res) => {
              if (res.status !== 200)
                return MessageAlert.warning('장바구니 추가에 실패하였습니다.');
              MessageAlert.success('장바구니에 추가되었습니다.');
              userDataAction.updateData('cartList', isSame, res.data);
            });
          } else {
            await handleMutate(createCart, mutateData).then((res) => {
              if (res.status !== 200)
                return MessageAlert.warning('장바구니 추가에 실패하였습니다.');
              MessageAlert.success('장바구니에 추가되었습니다.');
              userDataAction.addData('cartList', res.data);
            });
          }
        }
        return;
      }
      case 'wishlist': {
        mutateData = { user_id: userInfo.user_id, product_id: d.key };
        await handleMutate(createWishlist, mutateData).then((res) => {
          if (res.status !== 200)
            return MessageAlert.warning('찜목록에 추가에 실패하였습니다.');
          MessageAlert.success('찜목록에 추가되었습니다.');
          userDataAction.addData('wishlist', res.data);
        });
        return;
      }
      case 'remove': {
        mutateData = wishlist.find((wish) => wish.product_id === d.key);
        await handleMutate(deleteWishlist, mutateData.wishlist_id).then(
          (res) => {
            if (res.status !== 200)
              return MessageAlert.warning('찜목록에서 삭제 실패');
            MessageAlert.success('찜목록에서 삭제되었습니다.');
            userDataAction.removeData('wishlist', 'wishlist_id', mutateData);
          }
        );
        return;
      }
      default:
        return MessageAlert.warning(
          `${t === 'cart' ? '장바구니 추가' : '찜하기'}에 실패하였습니다`
        );
    }
  };

  const handleMutate = async (mutateFn, mutate) => {
    try {
      const res = await mutateFn(mutate);
      setModalData({ type: '', data: null });
      return res;
    } catch (err) {
      console.error('[Product handleMutate] Error !! ', err);
      throw err;
    }
  };

  /**
   * 상품 목록 정제
   * --
   */
  const ProductList = useMemo(() => {
    return products.map((product) => {
      const thumb = product.product_file.find(
        (file) => file.type === 'THUMBNAIL'
      );
      const tags = product.tag.map((tag) => ({
        id: tag.tag_id,
        label: tag.tag_name,
      }));

      // 쇼핑몰 추출
      const shopData = shops.find(
        (shop) => shop.shop_information_id === product.shop_information_id
      );

      return {
        key: product.product_id,
        title: product.product_name,
        price: product.price,
        isSoldOut:
          product.num === 0 || product.product_status === 'SOLDOUT' ? 'Y' : 'N',
        src: thumb ? `${STORE_URL}/${thumb.file_url}` : null,
        tags,
        brand: shopData ? shopData.shop_information_name : '',
        shopKey: product.shop_information_id,
        discount: Promotion ? Promotion.discount_rate : 0,
        discount_type: Promotion ? Promotion.discount_type : 'W',
        finalPrice: Promotion
          ? product.price - calculateDiscount(product.price, Promotion).discount
          : product.price,
        isWish: wishlist.some((wish) => wish.product_id === product.product_id),
        create: product.created_at,
        score: product.average_score,
        comment: product.review_count,
        sales: 0,
        options: product.product_option,
        type: product.type,
        policy: Promotion,
        packaging: product.packaging_method,
      };
    });
  }, [products, wishlist, Promotion, shops]);

  /**
   * 프로모션/그룹상품 데이터 정제
   * --
   */
  const PromotionData = React.useMemo(() => {
    if (!Promotion && !Group && !ProductList.length) return null;

    const standardData = type === 'recipe' ? Group : Promotion;

    const newData = {
      ...standardData,
      products: !standardData?.map_product_policy?.length
        ? []
        : standardData?.map_product_policy
            .filter((_item) => _item.is_enable === 'Y')
            .map((_item) => {
              const findProduct = ProductList.find(
                (product) => product.key === _item.product_id
              );

              console.log('findProduct ==>', findProduct);

              if (!findProduct) return null;
              else
                return {
                  ..._item,
                  ...findProduct,
                };
            })
            .filter((_item) => _item),
    };

    return newData;
  }, [ProductList, Promotion, Group, type]);

  /**
   * 장바구니 수량
   * --
   */
  const CartNum = useMemo(() => {
    return cartList.reduce((acc, cur) => acc + cur.cart_num, 0);
  }, [cartList]);

  /**
   * 정렬 아이템
   * --
   */
  const SortItems = useMemo(() => {
    if (!settings) return TAP;
    const { product_standard, product_order_by } = settings;
    let standard = `${product_order_by}-finalPrice`;
    if (product_standard === 'NAME') standard = `${product_order_by}-title`;
    if (product_standard === 'PRICE') standard = `${product_order_by}-price`;
    if (product_standard === 'REGISTER')
      standard = `${product_order_by}-create`;
    return [{ key: standard, label: '추천순' }, ...TAP];
  }, [settings]);

  return (
    <PromotionDetailPresenter
      navigate={navigate}
      location={location}
      promotion={promotion}
      products={ProductList}
      modalData={modalData}
      setModalData={setModalData}
      user={userInfo}
      cartList={CartNum}
      onSubmit={handleSubmit}
      sortItems={SortItems}
      screenSize={screenSize}
      data={PromotionData}
      type={type.toLocaleUpperCase()}
    />
  );
};

export default PromotionDetailContainer;
